import { Injectable, Injector } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AppComponentBase } from "@shared/common/app-component-base";
import { UniversalSearchServiceProxy } from "@shared/service-proxies/service-proxies";
import { AppNavigationService } from "../app-navigation.service";
import { AppMenuItem } from "../app-menu-item";
import {
    UNIVERSAL_SEARCH_CATEGORY_PERMISSIONS,
    UNIVERSAL_SEARCH_EMPLOYEE_PERMISSIONS,
    UNIVERSAL_SEARCH_MAX_PER_CATEGORY,
    UniversalSearchResultItem,
} from "./universal-search.models";
import { AppRoutes } from "@app/shared/routes/app-routes";
import { groupSearchResults, isSearchQueryReady, matchesSearchQuery, normalizeSearchQuery } from "./universal-search.utils";

export type UniversalSearchResponse = {
    groups: ReturnType<typeof groupSearchResults>;
    items: UniversalSearchResultItem[];
    error: boolean;
};

@Injectable()
export class UniversalSearchService extends AppComponentBase {
    constructor(
        injector: Injector,
        private _appNavigationService: AppNavigationService,
        private _universalSearchProxy: UniversalSearchServiceProxy,
    ) {
        super(injector);
    }

    search(rawQuery: string): Observable<UniversalSearchResponse> {
        const query = normalizeSearchQuery(rawQuery);
        if (!isSearchQueryReady(query)) {
            return of({ groups: [], items: [], error: false });
        }

        const localItems = this.searchNavigation(query);

        return this._universalSearchProxy.search(query, UNIVERSAL_SEARCH_MAX_PER_CATEGORY).pipe(
            map((result) => {
                const entityItems = (result?.items ?? [])
                    .map((item) => this.mapEntityItem(item))
                    .map((item) => this.normalizeEmployeeResult(item))
                    .filter((item) => this.canSearchCategory(item.category));
                const items = this.dedupeResults([...localItems, ...entityItems]);
                return {
                    items,
                    groups: groupSearchResults(items),
                    error: false,
                };
            }),
            catchError(() =>
                of({
                    items: localItems,
                    groups: groupSearchResults(localItems),
                    // Surface error when entity search fails even if local pages matched.
                    error: true,
                }),
            ),
        );
    }

    /** Pages/Subsections use menu showMenuItem; entity categories use list-page permissions. */
    private canSearchCategory(category: string): boolean {
        if (category === "Employees") {
            return UNIVERSAL_SEARCH_EMPLOYEE_PERMISSIONS.some((permission) => this.permission.isGranted(permission));
        }

        const permission = UNIVERSAL_SEARCH_CATEGORY_PERMISSIONS[category];
        if (!permission) {
            return true;
        }
        return this.permission.isGranted(permission);
    }

    /**
     * Own employee hits must open My Profile, not admin edit-user
     * (admin route requires User CRUD which employees typically lack).
     */
    private normalizeEmployeeResult(item: UniversalSearchResultItem): UniversalSearchResultItem {
        if (item.category !== "Employees") {
            return item;
        }

        const currentUserId = this.appSession?.userId;

        const rawId = item.navigationState?.["id"] ?? String(item.id ?? "").replace(/^Employees-/i, "");

        const selectedEmployeeId = Number(rawId);

        const isSelf = currentUserId != null && !Number.isNaN(selectedEmployeeId) && Number(currentUserId) === selectedEmployeeId;

        // Logged-in employee → My Profile
        if (isSelf) {
            return {
                ...item,
                route: AppRoutes.myProfile,
                navigationState: undefined,
                queryParams: undefined,
            };
        }

        // Other employee → that employee's Time and Attendance dashboard
        // (Productivity / Pending / Approved / Declined).
        if (this.permission.isGranted("Pages.Activity.ReamTimeTracking") || this.permission.isGranted("Pages.Activity.TimeAndAttendence")) {
            return {
                ...item,
                route: AppRoutes.activitiesTimeAndAttendance,
                navigationState: {
                    ...item.navigationState,
                    id: selectedEmployeeId,
                },
                queryParams: {
                    User: String(selectedEmployeeId),
                },
            };
        }

        // No permission:
        // Keep the original employee result so the navigation layer
        // can handle the unauthorized access/permission message.
        return item;
    }
    private searchNavigation(query: string): UniversalSearchResultItem[] {
        const entries: UniversalSearchResultItem[] = [];
        const menu = this._appNavigationService.getMenu();
        (menu.items ?? []).forEach((item) => this.collectNavigationItems(item, 0, query, entries));
        return this.limitByCategory(this.dedupeResults(entries));
    }

    private collectNavigationItems(item: AppMenuItem, depth: number, query: string, entries: UniversalSearchResultItem[]): void {
        if (item.route && this._appNavigationService.showMenuItem(item)) {
            const title = this.l(item.name);
            const matchText = [title, item.name, item.route, ...(item.searchTags ?? [])].join(" ");
            if (matchesSearchQuery(matchText, query)) {
                entries.push({
                    id: `navigation-${item.route}`,
                    category: depth === 0 ? "Pages" : "Subsections",
                    title,
                    route: item.route,
                    icon: item.icon || undefined,
                    customImage: item.customImage || undefined,
                    matchText,
                });
            }
        }

        (item.items ?? []).forEach((child) => this.collectNavigationItems(child, depth + 1, query, entries));
    }

    private mapEntityItem(item: {
        id: string;
        category: string;
        title: string;
        subtitle?: string;
        route: string;
        navigationState?: Record<string, unknown>;
        queryParams?: Record<string, string>;
    }): UniversalSearchResultItem {
        return {
            id: `${item.category}-${item.id}`,
            category: item.category,
            title: item.title,
            subtitle: item.subtitle,
            route: item.route,
            navigationState: item.navigationState,
            queryParams: item.queryParams,
            matchText: `${item.title} ${item.subtitle ?? ""}`,
        };
    }

    private dedupeResults(items: UniversalSearchResultItem[]): UniversalSearchResultItem[] {
        const seen = new Set<string>();
        const result: UniversalSearchResultItem[] = [];
        for (const item of items) {
            const key = `${item.category}|${item.route}|${item.title}`;
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            result.push(item);
        }
        return result;
    }

    private limitByCategory(items: UniversalSearchResultItem[]): UniversalSearchResultItem[] {
        const counts = new Map<string, number>();
        const limited: UniversalSearchResultItem[] = [];
        for (const item of items) {
            const count = counts.get(item.category) ?? 0;
            if (count >= UNIVERSAL_SEARCH_MAX_PER_CATEGORY) {
                continue;
            }
            counts.set(item.category, count + 1);
            limited.push(item);
        }
        return limited;
    }
}
